import React, { useState, useEffect, useContext } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import ConfirmationPopup from "../deletePopup/deletePopup";
import {
  faTrashCan,
  faChevronLeft,
  faChevronRight,
  faPenToSquare,
  faPlus,
  faSearch,
  faEye,
  faIdCard,
  faSlidersH,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { downloadExport } from "../../api/apiHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { BGContext } from "../context/bgprofileContext";
import { AppContext } from "../context/appContext";
import FilterPanel from "../common/FilterPanel";
import FilterChips, { countActive } from "../common/FilterChips";
import { BG_PROFILE_FILTERS } from "../../config/filterConfigs";
import { useLocation, useNavigate } from "react-router-dom";

const sampleFilterValues = {
  gender: "",
  birthLocation: "",
  residentLocation: "",
  religiousBelief: "",
  motherTongue: "",
  qualification: "",
  professionSector: "",
  professionalDesignation: "",
  annualIncome: "",
  familyType: "",
  dietType: "",
  subscription: "",
  isApproved: "",
  startDate: "",
  endDate: "",
  maritalStatus: "",
  ownerRelationship: "",
};

// Master-data refs (religion, caste, …) come back POPULATED as objects
// ({_id, religion, …} / {_id, caste, religionId, …}) from the list/search/filter
// endpoints. Render the name field defensively so a populated object never reaches
// JSX — React throws "Objects are not valid as a React child" otherwise. Handles
// object | string | null uniformly.
const mstText = (v, key) =>
  v == null ? "" : typeof v === "object" ? (v[key] ?? "") : String(v);

const ProfilesListing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { matchseekerMasterData } = useContext(AppContext);
  const {
    bgSearchTerm: searchTerm,
    setBgSearchTerm: setSearchTerm,
    bgFilters: globalFilters,
    setBgFilters: setGlobalFilters,
    ContextCurrentPage: currentPage,
    contextSetCurrentPage: setCurrentPage,
    ContextTotalPages: totalPages,
    contextSetTotalPages: setTotalPages,
    Contextlimit: limit,
    ContextReload: reload,
    ContextSetReload: setReload,
  } = useContext(BGContext);

  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(true);
  const [deleteUserData, setDeleteUserData] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    const fetchProfiles = async () => {
      setLoading(true);
      try {
        const term = (searchTerm || "").trim();
        let res;
        if (term) {
          // Free-text search across name, phone, religion, caste, sub-caste,
          // location, profession, income + owner email/name (server-side).
          res = await func.get(urls.SEARCH_BG_PROFILES_URL, {
            params: { query: term, page: currentPage, limit },
            signal: controller.signal,
          });
        } else {
          // Collect only the filters actually set. AsyncSelect values are
          // { label, value:id }; plain selects/inputs are strings.
          const modifiedFilters = {};
          Object.keys(globalFilters).forEach((key) => {
            const v = globalFilters[key]?.value || globalFilters[key];
            if (v) {
              // modal key `designation` → schema field `professionalDesignation`
              const field = key === "designation" ? "professionalDesignation" : key;
              modifiedFilters[field] = v;
            }
          });
          if (Object.keys(modifiedFilters).length > 0) {
            // Active filters → real filter endpoint (list endpoint ignores filters).
            res = await func.post(urls.FILTER_BG_PROFILES_URL, modifiedFilters, {
              params: { page: currentPage, limit },
              signal: controller.signal,
            });
          } else {
            res = await func.post(urls.LIST_BG_PROFILES_URL, {}, {
              params: { page: currentPage, limit },
              signal: controller.signal,
            });
          }
        }
        if (res?.status === 200) {
          setFilteredProfiles(res?.data?.bgProfiles || res?.data?.data || []);
          setTotalPages(res.data.totalPages || 1);
        } else {
          toast.error("Error fetching profiles");
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        toast.error("Network error");
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
    return () => controller.abort();
  }, [reload, currentPage]);

  const handleSearch = () => { setReload(!reload); setCurrentPage(1); };

  const applyFilters = () => { setReload(!reload); setShowFilter(false); setCurrentPage(1); setSearchTerm(""); };

  const resetFilters = () => {
    setGlobalFilters(sampleFilterValues);
    setSearchTerm("");
    setReload(!reload);
    setCurrentPage(1);
  };

  const clearFilterKeys = (keys) => {
    const next = { ...globalFilters };
    keys.forEach((k) => { next[k] = ""; });
    setGlobalFilters(next);
    setReload(!reload);
    setCurrentPage(1);
  };

  const handleAccept = async () => {
    setShowDeleteButton(false);
    if (filteredProfiles.length === 1) setCurrentPage(Math.max(1, currentPage - 1));
    const res = await func.del(urls.DELETE_BGPROFILE_URL + deleteUserData._id);
    setShowDeleteButton(true);
    if (res?.status === 200) {
      toast.success("Profile Deleted Successfully");
      setDeleteUserData({});
      setReload(!reload);
      setShowDeletePopup(false);
    } else {
      toast.warn(res);
    }
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p >= currentPage - 2 && p <= currentPage + 2
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6 mt-14 rounded-xl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faIdCard} className="text-white text-xl" />
            <div>
              <h1 className="text-2xl font-bold text-white">Bride / Groom Profiles</h1>
              <p className="text-white/70 text-sm">All registered BG profiles</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilter(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faSlidersH} />
              Filters
              {countActive(BG_PROFILE_FILTERS, globalFilters) > 0 && (
                <span className="ml-1 inline-flex items-center justify-center text-[10px] font-bold bg-white text-indigo-700 rounded-full w-5 h-5">
                  {countActive(BG_PROFILE_FILTERS, globalFilters)}
                </span>
              )}
            </button>
            <button
              onClick={async () => {
                try {
                  const today = new Date().toISOString().slice(0, 10);
                  const modFilters = {};
                  Object.keys(globalFilters).forEach((k) => {
                    modFilters[k] = globalFilters[k]?.value || globalFilters[k] || undefined;
                  });
                  await downloadExport('mst-admin/export/bg-profiles', {
                    search: searchTerm || undefined,
                    gender: modFilters.gender || undefined,
                  }, `bg_profiles_${today}.csv`);
                } catch { toast.error('Export failed'); }
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition border border-white/20"
            >
              <FontAwesomeIcon icon={faDownload} />
              Export All
            </button>
            <button
              onClick={() => navigate("/user-management/bride-groom-profile/add")}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Profile
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-sm">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); if (!e.target.value.trim()) handleSearch(); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              maxLength={60}
              placeholder="Search by any field..."
              className="w-full pl-9 pr-3 py-2 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow"
          >
            Search
          </button>
        </div>

        <FilterChips
          config={BG_PROFILE_FILTERS}
          filters={globalFilters}
          onClearKeys={clearFilterKeys}
          onClearAll={resetFilters}
        />

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Name", "Mobile / DOB", "Religion / Caste", "Created / Updated", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(6)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : filteredProfiles.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faIdCard} className="text-3xl mb-3 block mx-auto opacity-20" />
                      No profiles found
                    </td>
                  </tr>
                ) : (
                  filteredProfiles.map((profile, index) => (
                    <tr key={profile._id || index} className="border-b border-gray-100 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-gray-400 text-xs">{(currentPage - 1) * limit + index + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{[profile.firstName, profile.lastName].filter(Boolean).join(" ") || "—"}</td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{profile.contactNumber || profile.ownerPhone || "—"}</p>
                        <p className="text-xs text-gray-400">{profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString("en-IN") : ""}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-gray-700">{mstText(profile?.religion, "religion") || "—"}</p>
                        <p className="text-xs text-gray-400">{mstText(profile?.caste, "caste")}{profile?.subCaste ? ` · ${profile.subCaste}` : ""}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        <p>{new Date(profile.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                        <p className="text-gray-400">{new Date(profile.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => navigate(`/user-management/bride-groom-profile/view/${profile._id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View
                          </button>
                          <button
                            onClick={() => navigate(`/user-management/bride-groom-profile/edit/${profile._id}`)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                            Edit
                          </button>
                          <button
                            onClick={() => { setDeleteUserData(profile); setShowDeletePopup(true); }}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded-md font-medium transition"
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-5">
            <PagBtn onClick={() => setCurrentPage(1)} disabled={currentPage === 1} label="«" />
            <PagBtn onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} icon={faChevronLeft} />
            {currentPage > 3 && <span className="px-2 text-white/60">…</span>}
            {pages.map((p) => (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`w-8 h-8 rounded-md text-sm font-medium transition ${
                  p === currentPage ? "bg-white text-indigo-700" : "text-white/70 hover:bg-white/10"
                }`}
              >
                {p}
              </button>
            ))}
            {currentPage < totalPages - 2 && <span className="px-2 text-white/60">…</span>}
            <PagBtn onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} icon={faChevronRight} />
            <PagBtn onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} label="»" />
          </div>
        )}
      </div>

      {/* Delete Popup */}
      <ConfirmationPopup
        show={showDeletePopup}
        onClose={() => setShowDeletePopup(false)}
        onCancel={() => setShowDeletePopup(false)}
        onAccept={handleAccept}
        showDeleteButton={showDeleteButton}
        question={<>Are you sure you want to delete <span className="font-bold italic">{deleteUserData?.firstName} {deleteUserData?.lastName}</span>?</>}
        title="Delete Confirmation"
        acceptText="Yes, Delete"
        cancelText="Cancel"
      />

      <FilterPanel
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        title="Filter Profiles"
        config={BG_PROFILE_FILTERS}
        filters={globalFilters}
        onChange={setGlobalFilters}
        onApply={applyFilters}
        onReset={resetFilters}
        masterData={matchseekerMasterData}
      />
    </div>
  );
};

const PagBtn = ({ onClick, disabled, label, icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition ${
      disabled ? "text-white/20 cursor-not-allowed" : "text-white/70 hover:bg-white/10"
    }`}
  >
    {icon ? <FontAwesomeIcon icon={icon} className="text-xs" /> : label}
  </button>
);

export default ProfilesListing;
